"use client";

import * as React from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel, // Include if pagination controls are added here
  useReactTable,
  PaginationState,
  Updater
} from '@tanstack/react-table';

// Augment the TanStack Table types to include our custom style property
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends unknown, TValue extends unknown> {
    style?: React.CSSProperties;
  }
}

interface SimpleTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  // Optional props for pagination state if handled internally
  pagination?: PaginationState;
  pageCount?: number;
  onPaginationChange?: (updater: Updater<PaginationState>) => void;
  // Add other optional props like enableSorting, enableFiltering if needed
  isLoading?: boolean; // Add loading state prop
  skeletonRowCount?: number; // Number of skeleton rows to show
}

export function SimpleTable<TData, TValue>({
  columns,
  data,
  pagination,
  pageCount,
  onPaginationChange,
  isLoading = false,
  skeletonRowCount = 10 // Default skeleton rows
}: SimpleTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    pageCount: pageCount ?? -1, // Use -1 if pageCount is not provided
    state: {
      pagination,
    },
    onPaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(), // Include if pagination controls are rendered here
    manualPagination: !!onPaginationChange, // Assume manual pagination if handler is provided
    // Add other options like manualSorting, manualFiltering if needed
  });

  // Render skeleton rows
  const renderSkeletons = (_: number) => (
    <tbody>
      {Array.from({ length: skeletonRowCount }).map((_, index) => (
        <tr key={index} className="border-transparent animate-pulse">
          {columns.map((column, colIndex) => (
            <td 
              key={colIndex} 
              className="p-4"
              // Apply style from column meta
              style={column.meta?.style} 
            >
              <div className="skeleton h-4 w-full"></div>
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );

  return (
    <div className="overflow-x-auto border-transparent">
      <table className="table table-sm w-full">
        {/* Head */}
        <thead className="bg-base-300/50 backdrop-blur-xl">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="border-b border-base-300">
              {headerGroup.headers.map((header) => (
                <th 
                  key={header.id} 
                  className="p-4 font-semibold"
                  // Apply style from column meta
                  style={header.column.columnDef.meta?.style}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        {/* Body */}
        {isLoading ? renderSkeletons(columns.length) : (
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-base-200/50 border-b border-base-100 even:bg-base-300/30">
                {row.getVisibleCells().map((cell) => (
                  <td 
                    key={cell.id} 
                    className="p-4"
                    // Apply style from column meta
                    style={cell.column.columnDef.meta?.style}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
            {table.getRowModel().rows.length === 0 && !isLoading && (
              <tr>
                <td colSpan={columns.length} className="text-center p-10 opacity-50">
                  No data available.
                </td>
              </tr>
            )}
          </tbody>
        )}

        {/* Footer - Optional: Add pagination controls here if needed */}
        {/*
        <tfoot>
           <tr><td colSpan={columns.length}>Pagination Controls Go Here</td></tr>
        </tfoot>
         */}
      </table>
       {/* Simple External Pagination Example (if onPaginationChange is provided) */}
      {onPaginationChange && pageCount && pageCount > 1 && pagination && (
        <div className="flex items-center justify-between p-2 border-t border-base-300">
           <span className="text-sm opacity-70">
              Page {pagination.pageIndex + 1} of {pageCount}
            </span>
           <div className="join">
              <button
                  className="join-item btn btn-sm btn-outline"
                  onClick={() => onPaginationChange(old => ({ ...old, pageIndex: 0 }))}
                  disabled={pagination.pageIndex === 0}
              >
                  First
              </button>
              <button
                  className="join-item btn btn-sm btn-outline"
                  onClick={() => onPaginationChange(old => ({ ...old, pageIndex: old.pageIndex - 1 }))}
                  disabled={pagination.pageIndex === 0}
              >
                  Previous
              </button>
              <button
                  className="join-item btn btn-sm btn-outline"
                  onClick={() => onPaginationChange(old => ({ ...old, pageIndex: old.pageIndex + 1 }))}
                  disabled={pagination.pageIndex >= pageCount - 1}
              >
                  Next
              </button>
              <button
                  className="join-item btn btn-sm btn-outline"
                  onClick={() => onPaginationChange(old => ({ ...old, pageIndex: pageCount - 1 }))}
                  disabled={pagination.pageIndex >= pageCount - 1}
              >
                  Last
              </button>
           </div>
        </div>
      )}
    </div>
  );
} 